import { track } from '@tldraw/tldraw'
import { FontWeightProps } from './Types'
import { useUpdateStyle } from '../VisualEditor/hooks/useUpdateStyle'
import { useState } from 'react'
import { ComboBox, ComboBoxProps } from 'components/ComboBox/ComboBox'
import { FontWeightStyle } from '../VisualEditor/StyleProps'
import { Item } from '@react-stately/collections'
import { FONT_WEIGHTS } from '../Shapes/TextBoxShape/constants'
import { observer } from 'mobx-react-lite'
import Styles from './FontWeight.module.scss'
import { useAvailableWeights } from '../VisualEditor/hooks/useAvailableWeights'
import { setProseWeight, useActiveProseMarks } from '../Shapes/TextBoxShape/activeProseView'
import { FontWeightType } from '../VisualEditor/Types'

const FontWeight = track(
  observer(({ editor, style, fontFamilyStyle, italicStyle }: FontWeightProps) => {
    const [isSearching, setIsSearching] = useState(false)
    const updateStyle = useUpdateStyle(editor)
    const availableWeights = useAvailableWeights(fontFamilyStyle, italicStyle)
    const proseMarks = useActiveProseMarks()

    // While editing text, the control reflects the selection's per-run weight: a number when uniform,
    // Mixed when runs differ, and the whole-shape weight when no run sets one explicitly (null).
    const displayStyle: FontWeightProps['style'] = ((): FontWeightProps['style'] => {
      if (!proseMarks) return style
      if (typeof proseMarks.weight === 'number') return { type: 'shared', value: proseMarks.weight as FontWeightType }
      if (proseMarks.weight === 'mixed') return { type: 'mixed' }
      return style
    })()

    // Picking any weight while editing sets it as a per-run mark on the selection (the whole channel bold
    // uses); only when no textBox is being edited does it fall through to the whole-shape style.
    const applyWeight = (weight: number) => {
      if (setProseWeight(weight)) return
      updateStyle(FontWeightStyle, weight)
    }

    const baseProps: Partial<ComboBoxProps> = {
      'aria-label': 'Font weight',
      size: 'md',
      wrapperClassName: Styles.comboboxWrapper,
      contentsClassName: Styles.comboboxContents,
      inputClassName: Styles.comboboxInput,
    }

    if (!isSearching) {
      return (
        <span>
          <ComboBox
            {...baseProps}
            onFocus={() => setIsSearching(true)}
            inputValue={displayStyle.type === 'shared' ? FONT_WEIGHTS[displayStyle.value] : 'Mixed'}
          >
            <Item>Mixed</Item>
          </ComboBox>
        </span>
      )
    }

    return (
      <ComboBox
        {...baseProps}
        autoFocus
        menuTrigger="focus"
        onBlur={() => setIsSearching(false)}
        onSelectionChange={(newWeight) => {
          if (!newWeight) return
          applyWeight(parseInt(newWeight as string, 10))
          setIsSearching(false)
        }}
        selectedKey={displayStyle.type === 'shared' ? displayStyle.value : 'Mixed'}
        defaultInputValue=""
      >
        {availableWeights.map((weight) => {
          const textValue = FONT_WEIGHTS[weight]
          return (
            <Item key={weight} textValue={textValue}>
              <span style={{ fontWeight: weight }}>{textValue}</span>
            </Item>
          )
        })}
      </ComboBox>
    )
  }),
)

export { FontWeight }
